import modalTemplateUrl from './common/templates/modal.ngtemplate.html';
import notificationTemplateUrl from './common/templates/notification.ngtemplate.html';
import selectTemplateUrl from './common/templates/select.ngtemplate.html';

import ReactGA from 'react-ga4';
import _ from 'lodash';
import {
  getAngularService,
  AngularServices,
} from '../modules/core/helpers/get-angular-service';
import config from './env.config';
import { registerGoogleAnalytics } from './global.tracking';
import { shouldIncludeSegmentAnonymousId } from './tracking/segment-endpoints';

export default angular.module('doxy.global.config', []).name;

Object.keys(config).forEach((key) => {
  angular.module('doxy.global.config').constant(key, config[key]);
});

/**
 * List of the names of the states from `src/app/global/global.router.js`
 * that a patient can visit (in-visit, waiting room as patient, patient app).
 * When the app is in one of these states, we send X-Doxyme-User-Role: patient
 * so the backend does not log client IP (privacy).
 */
const patientFacingStates = new Set(['home', 'wrongRoom', 'patient']);

/** Header sent in patient context so backend does not log client IP. Value is case-insensitive. */
export const X_DOXYME_USER_ROLE_HEADER = 'X-Doxyme-User-Role';
export const PATIENT_ROLE_VALUE = 'patient';

angular
  .module('doxy.global.config')
  .config(function (LoopBackResourceProvider, apiUrl) {
    LoopBackResourceProvider.setUrlBase(apiUrl + '/api');
  })
  .config(function ($compileProvider) {
    $compileProvider.aHrefSanitizationWhitelist(/^\s*(https?|blob|mailto):/);
  })
  .config(function (LogglyLoggerProvider, env) {
    if (env === 'development') return;
    const tags = ['frontend', env].join(',');
    LogglyLoggerProvider.inputToken('6cf94562-2d8e-4467-aea1-e9f0051937cd')
      .includeUrl(true)
      .includeTimestamp(true)
      .inputTag(tags)
      .level('INFO')
      .sendConsoleErrors(false)
      .fields({
        platform: window.platform,
      });
  })
  .config(function ($modalProvider) {
    angular.extend($modalProvider.defaults, {
      html: true,
      templateUrl: modalTemplateUrl,
      backdrop: true,
      backdropAnimation: 'am-fade',
      animation: 'am-slide-top',
    });
  })
  .config(function ($analyticsProvider) {
    $analyticsProvider.virtualPageviews(false);
  })
  .config(function (usSpinnerConfigProvider) {
    usSpinnerConfigProvider.setDefaults({ color: '#5f6a7d' });
  })
  .config(function (notificationsConfigProvider) {
    notificationsConfigProvider.setAcceptHTML(true);
    notificationsConfigProvider.setTemplateUrl(notificationTemplateUrl);
  })
  .config(function ($httpProvider) {
    $httpProvider.defaults.timeout = 30 * 1000;
    $httpProvider.defaults.useXDomain = true;
    $httpProvider.useApplyAsync(true);
    delete $httpProvider.defaults.headers.common['X-Requested-With'];
    $httpProvider.interceptors.push(function ($injector) {
      const logNetworkError = (rejection) => {
        $injector
          .get('$log')
          .error(
            `DoxymeNetworkError: ${rejection.status}`,
            _.pick(rejection.config, ['url', 'method']),
            rejection.data
          );
      };

      return {
        request(req) {
          // API request timeout
          req.timeout = req.timeout || $httpProvider.defaults.timeout;
          const overrideSecret = localStorage.getItem('overrideSecret');
          const overrideIP = localStorage.getItem('overrideIP');
          if (overrideIP && overrideSecret) {
            req.headers['x-override-ip'] = overrideIP;
            req.headers['x-override-secret'] = overrideSecret;
          }
          // Patient context: backend must not log client IP (privacy). Send header only for our API.
          // Use transition flag so requests fired during resolve (e.g. Room.findBySlug) get the header
          // before $state.current is updated.
          try {
            const $state = $injector.get('$state');
            const $rootScope = $injector.get('$rootScope');
            const apiUrl = $injector.get('apiUrl');
            const isOurApi =
              (typeof req.url === 'string' &&
                (req.url.indexOf(apiUrl) === 0 || req.url.indexOf('/api/') === 0));
            const inPatientContext =
              ($state.current?.name && patientFacingStates.has($state.current.name)) ||
              $rootScope.__patientContextForRequests === true;
            if (isOurApi && inPatientContext) {
              req.headers[X_DOXYME_USER_ROLE_HEADER] = PATIENT_ROLE_VALUE;
            }
          } catch (err) {
            console.debug(
              'X-Doxyme-User-Role: skip ($state/apiUrl unavailable, e.g. during bootstrap)',
              err
            );
          }
          // Add Segment anonymousId header for configured endpoints
          if (shouldIncludeSegmentAnonymousId(req.url, req.method)) {
            try {
              const trackingFactory = $injector.get('trackingFactory');
              if (
                trackingFactory?.segment != null &&
                typeof trackingFactory.segment.getAnonymousId === 'function'
              ) {
                const anonymousId = trackingFactory.segment.getAnonymousId();
                if (anonymousId != null) {
                  req.headers['x-segment-anonymous-id'] = anonymousId;
                }
              }
            } catch {
              // Silently fail - anonymousId is optional
            }
          }

          // VM 03/27/18
          // Quick workaround: send unauthorized request (clear "authorization" header)
          // for some public endpoints to avoid 401 "Invalid access token" response
          if (req.params && req.params.$_clearAuthorization && req.headers) {
            delete req.headers.authorization;
            delete req.params.$_clearAuthorization;
          }
          return req;
        },
        responseError(rejection) {
          const isUser = !!$injector.get('User').getCurrentId();
          const Auth = $injector.get('Auth');

          switch (true) {
            case rejection.status === 401:
              if (!isUser) break;
              $injector
                .get('notificationFactory')
                .showCommon('warning', 'Session expired: please log in again');
              Auth.clearCurrentUser();
              getAngularService(
                AngularServices.LogOutFactory
              ).cleanUpAndRedirect('signIn');
              break;
            case rejection.status === 444:
              Auth.clearCurrentUser();
              if ($injector.get('$window').location.pathname !== '/blocked') {
                $injector.get('$window').location.href = '/blocked';
              }
              break;
            case rejection.status >= 500:
              $injector.get('$rootScope').$broadcast('http:error:500');
              $injector.get('notificationFactory').showCommon('error', '500');
              logNetworkError(rejection);
            case rejection.status === -1:
              $injector.get('$rootScope').$broadcast('http:error:500');
          }

          return Promise.reject(rejection);
        },
      };
    });
  })
  .run(function ($rootScope, $http, trackingFactory, User) {
    $rootScope.$once('$stateChangeSuccess', (ev, toState) => {
      if (!patientFacingStates.has(toState.name)) {
        registerGoogleAnalytics();
        trackingFactory.global.initProvider(User.isAuthenticated());
      }
      delete $http.defaults.timeout;
    });
    $rootScope.$once('$stateChangeError', () => {
      delete $http.defaults.timeout;
    });
  })
  .run(function ($rootScope, $window) {
    // Set initial patient context from host so the first API request (e.g. themeService getCurrentInstitution
    // before any transition) gets X-Doxyme-User-Role when we're on an institution subdomain (patient-facing).
    const host = ($window.location && $window.location.host) || '';
    const parts = host.split('.');
    const hasInstitutionSubdomain =
      parts.length >= 2 &&
      !['www', 'app'].includes((parts[0] || '').toLowerCase());
    $rootScope.__patientContextForRequests = hasInstitutionSubdomain;

    // Update patient context at transition start so API requests in state resolves (e.g. Room.findBySlug)
    // get X-Doxyme-User-Role: patient before $state.current is updated.
    $rootScope.$on('$stateChangeStart', (ev, toState) => {
      $rootScope.__patientContextForRequests = patientFacingStates.has(toState.name);
    });
  })
  .config(function ($provide) {
    $provide.decorator('$uiViewScroll', function ($document) {
      return function () {
        $document.scrollTo(0, 0, 500);
      };
    });

    $provide.decorator('$rootScope', function ($delegate) {
      const Scope = Object.getPrototypeOf($delegate);
      Scope.$once = function (name, listener) {
        const deregister = this.$on(name, function () {
          deregister();
          listener.apply(this, arguments);
        });

        return deregister;
      };

      return $delegate;
    });
  })
  .config(function ($logProvider, $injector) {
    $logProvider.debugEnabled($injector.get('env') !== 'production');
  })
  // Legacy compatibility config removed - preAssignBindingsEnabled was deprecated and removed in Angular 1.7+
  .config(function ($qProvider, $injector) {
    $qProvider.errorOnUnhandledRejections(
      $injector.get('env') !== 'production'
    );
  })
  .config(function ($selectProvider) {
    $selectProvider.defaults.template = selectTemplateUrl;
  })
  .run(function ($state) {
    $state.defaultErrorHandler((error) => {
      console.log(error);
    });
  })
  .run(function (themeService) {
    themeService.applyCurrentInstitutionTheme().catch((err) => {
      console.error('Apply theme error:', err);
    });
  })
  .run(function (doxyAnalyticsService) {
    doxyAnalyticsService.start();
  })
  .run(function ($rootScope) {
    // hard fix non clickable modals https://doxyme.atlassian.net/browse/CS-521
    const fixBackdropDuplicates = () =>
      setTimeout(() => {
        const backdrops = document.getElementsByClassName('modal-backdrop');
        if (
          backdrops.length > 1 &&
          !backdrops[0].classList.contains('ng-enter-active')
        ) {
          document.body.removeChild(backdrops[0]);
        }
      });

    $rootScope.$on('modal.show', fixBackdropDuplicates);
    $rootScope.$on('modal.hide', fixBackdropDuplicates);
  })
  .run((Browser) => {
    if (Browser.shouldEnableManifest()) {
      const link = document.createElement('link');
      link.setAttribute('href', '/manifest.json');
      link.setAttribute('crossorigin', 'use-credentials');
      link.setAttribute('rel', 'manifest');

      document.getElementsByTagName('head')[0].appendChild(link);
    }
  });
